/* eslint-disable @typescript-eslint/no-require-imports */

import { resolve } from 'path';
import { PartialProjectConfig } from '../../types';
import logger from '../logger';

/**
 * Loads and compiles a TypeScript configuration file using the user's local TypeScript installation
 */
export default function loadTypeScriptConfig(
  configPath: string,
): PartialProjectConfig {
  const projectRoot = process.cwd();

  try {
    logger.debug(`Attempting to load TypeScript config from: ${configPath}`);

    // Try to resolve TypeScript from the user's project
    let typescript: typeof import('typescript');
    try {
      // First try to require from the user's project directory
      const tsPath = resolve(projectRoot, 'node_modules', 'typescript');
      typescript = require(tsPath);
      logger.debug(`Found TypeScript in user's project at: ${tsPath}`);
    } catch {
      // Fall back to globally installed TypeScript or our dev dependency
      try {
        typescript = require('typescript');
        logger.debug('Using globally available TypeScript installation');
      } catch {
        throw new Error(
          `TypeScript is required to load .ts configuration files but was not found. ` +
            `Please install TypeScript in your project: npm install --save-dev typescript`,
        );
      }
    }

    // Read the TypeScript file

    const fs = require('fs');
    const sourceCode = fs.readFileSync(configPath, 'utf8');

    // Compile TypeScript to JavaScript
    const result = typescript.transpile(sourceCode, {
      target: typescript.ScriptTarget.ES2018,
      module: typescript.ModuleKind.CommonJS,
      moduleResolution: typescript.ModuleResolutionKind.NodeJs,
      esModuleInterop: true,
      allowSyntheticDefaultImports: true,
      resolveJsonModule: true,
      declaration: false,
      sourceMap: false,
    });

    // Create a temporary module to evaluate the compiled JavaScript
    const module = { exports: {} };
    const moduleWrapper = new Function(
      'module',
      'exports',
      'require',
      '__filename',
      '__dirname',
      result,
    );

    // Execute the compiled code
    moduleWrapper(
      module,
      module.exports,
      (id: string) => {
        // Custom require function that resolves relative to the config file
        if (id.startsWith('.')) {
          return require(resolve(configPath, '..', id));
        }
        return require(id);
      },
      configPath,
      resolve(configPath, '..'),
    );

    // Extract the configuration
    const config = (module.exports as any).default || module.exports;

    if (!config || typeof config !== 'object') {
      throw new Error(
        `TypeScript configuration file must export a configuration object. ` +
          `Received: ${typeof config}`,
      );
    }

    logger.debug('Successfully loaded and compiled TypeScript configuration');
    return config as PartialProjectConfig;
  } catch (error) {
    logger.error(
      `Failed to load TypeScript configuration from ${configPath}:`,
      error,
    );

    if (error instanceof Error) {
      // Provide more helpful error messages for common issues
      if (error.message.includes('Cannot find module')) {
        throw new Error(
          `Failed to load TypeScript configuration: ${error.message}. ` +
            `Make sure all imported modules are installed.`,
        );
      } else if (error.message.includes('TypeScript is required')) {
        throw error; // Re-throw our custom TypeScript missing error
      } else {
        throw new Error(
          `Failed to compile TypeScript configuration: ${error.message}. ` +
            `Please check your todesktop.ts file for syntax errors.`,
        );
      }
    }

    throw error;
  }
}
